/**
 * Inline PDF preview for Work files. Share the canvas renderer with the
 * full preview so browsers without a PDF plugin don't show a blank frame.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useOnScreen } from '@/hooks/useOnScreen';
import { openMessageFile } from '@/constants/utils/files';
import PdfJsViewer from '@/components/Files/PdfJsViewer.web';

const isPdf = (file) => {
  const mime = (file?.mimeType || '').toLowerCase();
  const name = (file?.name || '').toLowerCase();
  return mime === 'application/pdf' || name.endsWith('.pdf');
};

// Keep only nearby document buffers mounted. The fixed card height preserves
// scroll position when a preview is unmounted.
function PdfPreviewCard({ file, border, colors }) {
  const [containerRef, isOnScreen] = useOnScreen({ rootMargin: '300px' });
  const [failed, setFailed] = useState(false);
  const handleError = useCallback(() => setFailed(true), []);

  return (
    <View ref={containerRef} style={[styles.card, { borderColor: border }]}>
      <View style={[styles.header, { borderBottomColor: border, backgroundColor: colors.solid_color_block }]}>
        <Icon name="file-pdf" size={13} color={colors.text_color_secondary} />
        <Text size="xxs" color="secondary" numberOfLines={1} style={{ flex: 1, marginLeft: 6 }}>
          {file.name || 'document.pdf'}
        </Text>
        <Pressable accessibilityRole="button" accessibilityLabel={`Expand PDF preview for ${file.name || 'document.pdf'}`}
          onPress={() => openMessageFile(file)} style={styles.expand}>
          <Icon name="expand" size={14} color={colors.text_color_secondary} />
        </Pressable>
      </View>
      <View style={[styles.frame, { backgroundColor: colors.solid_color_block }]}>
        {isOnScreen && (failed ? (
          <View style={styles.error}>
            <Text size="sm" color="secondary">The PDF preview could not be rendered.</Text>
            <Pressable accessibilityRole="button" onPress={() => setFailed(false)} style={styles.retry}>
              <Text size="sm">Retry PDF preview</Text>
            </Pressable>
          </View>
        ) : <PdfJsViewer source={file.cdnPath} title={file.name} initialScale={0.5} onError={handleError} />)}
      </View>
    </View>
  );
}

export default function CoworkerDocumentPreview({ files }) {
  const { colors } = useTheme();
  const border = colors.border_color_secondary;

  const pdfs = useMemo(
    () => (Array.isArray(files) ? files.filter((f) => f && isPdf(f) && f.cdnPath) : []),
    [files],
  );

  if (pdfs.length === 0) return null;

  return (
    <View style={styles.container}>
      {pdfs.map((file) => (
        <PdfPreviewCard
          key={`${file.id || ''}:${file.cdnPath}`}
          file={file}
          border={border}
          colors={colors}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
    marginTop: 8,
    maxWidth: 440,
  },
  card: {
    borderRadius: 10,
    borderWidth: 1,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderBottomWidth: 1,
  },
  frame: {
    width: '100%',
    height: 380,
  },
  expand: { padding: 6, cursor: 'pointer' },
  error: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 16 },
  retry: { padding: 10, cursor: 'pointer' },
});
